
export interface Variant {
    key: string;
    name: string;
    short: string;
}

export interface Perf {
    name: string;
}

export interface Player {
    id: string;
    name: string;
    title: string | null;
    rating: number;
}

export interface Clock {
    initial: number;   // milliseconds
    increment: number; // milliseconds
}

export interface GameStateEvent {
    type: "gameState";
    moves: string; // space-separated UCI moves, e.g. "e2e4 c7c5"
    wtime: number; // white time remaining (ms)
    btime: number; // black time remaining (ms)
    winc: number;  // white increment (ms)
    binc: number;  // black increment (ms)
    wdraw?: boolean;
    bdraw?: boolean;
    wtakeback?: boolean;
    btakeback?: boolean;
    status: string; // "started" | "mate" | "draw" | etc.
}

export interface GameFullEvent {
    type: "gameFull";
    id: string;
    variant: Variant;
    speed: string;
    perf: Perf;
    rated: boolean;
    createdAt: number; // timestamp ms
    white: Player;
    black: Player;
    initialFen: string;
    clock: Clock;
    state: GameStateEvent;
}

export interface ChatLineEvent {
    type: "chatLine";
    username: string;
    text: string;
    room: "player" | "spectator";
}

export interface OpponentGoneEvent {
    type: "opponentGone";
    gone: boolean;
    claimWinInSeconds?: number; // time until claim possible
}

export type BoardStreamEvent =
    | GameFullEvent
    | GameStateEvent
    | ChatLineEvent
    | OpponentGoneEvent;


export class LichessClient {
    constructor(private httpRequest: HttpClient, private host: string) { }
    public async streamBoardGame(
        gameId: string,
        onGameFull: null | ((event: GameFullEvent) => void) = null,
        onGameState: null | ((event: GameStateEvent) => void) = null,
        onChatLine: null | ((event: ChatLineEvent) => void) = null,
        onOpponentGone: null | ((event: OpponentGoneEvent) => void) = null,
        signal?: AbortSignal,
    ): Promise<void> {
        const url = `${this.host}/api/board/game/stream/${gameId}`;
        const res = await this.httpRequest(url, {
            method: "GET",
            headers: { Accept: "application/x-ndjson" },
            signal,
        });
        if (!res.body) throw new Error("No stream body returned");

        const reader = res.body.getReader();
        const decoder = new TextDecoder();

        let buffer = "";
        try {
            while (true) {
                if (signal?.aborted) break;
                const { done, value } = await reader.read();
                if (done || signal?.aborted) break;
                buffer += decoder.decode(value, { stream: true });
                let newlineIndex: number;
                while ((newlineIndex = buffer.indexOf("\n")) !== -1) {
                    const line = buffer.slice(0, newlineIndex).trim();
                    buffer = buffer.slice(newlineIndex + 1);
                    if (!line) continue;

                    let evt: BoardStreamEvent;
                    try {
                        evt = JSON.parse(line);
                    } catch (err) {
                        console.error("Malformed stream line:", line, err);
                        continue;
                    }

                    switch (evt.type) {
                        case "gameFull":
                            if (onGameFull) onGameFull(evt as GameFullEvent);
                            break;
                        case "gameState":
                            if (onGameState) onGameState(evt as GameStateEvent);
                            break;
                        case "chatLine":
                            if (onChatLine) onChatLine(evt as ChatLineEvent);
                            break;
                        case "opponentGone":
                            if (onOpponentGone) onOpponentGone(evt as OpponentGoneEvent);
                            break;
                        default:
                            console.warn("Unexpected event type:", (evt as any).type);
                    }
                }
            }
        } finally {
            reader.cancel().catch(() => {});
        }
    }
    public async request(url: string, options?: RequestInit): Promise<Response> {
        return this.httpRequest(`${this.host}${url}`, options);
    }
    // public async streamEvents(onMessage: (msg: any) => void) {
    //     return this.openStream('/api/stream/event', {}, onMessage);
    // }

    // public async streamGame(gameId: string, onMessage: (msg: any) => void) {
    //     return this.openStream(`/api/board/game/stream/${gameId}`, {}, onMessage);
    // }

    public async getCurrentGames(): Promise<CurrentGame[]> {
        const res = await this.request('/api/account/playing');
        if (!res.ok) {
            throw new Error(`Failed to get current games: ${res.status} ${res.statusText}`);
        }
        let response = await res.json();
        return response.nowPlaying;
    }
    private async openStream(
        url: string,
        options: RequestInit,
        onMessage: (msg: any) => void
    ) {
        // Example NDJSON stream handling
        const res = await this.request(url, options);
        if (!res.body) throw new Error('No stream body returned');

        const reader = res.body.getReader();
        const decoder = new TextDecoder();

        let buffer = '';
        const readChunk = async () => {
            const { done, value } = await reader.read();
            if (done) return;
            buffer += decoder.decode(value, { stream: true });
            let boundary: number;
            while ((boundary = buffer.indexOf('\n')) >= 0) {
                const line = buffer.slice(0, boundary).trim();
                buffer = buffer.slice(boundary + 1);
                if (line) {
                    try {
                        onMessage(JSON.parse(line));
                    } catch (err) {
                        console.error('Stream parse error:', err);
                    }
                }
            }
            await readChunk();
        };
        readChunk();
    }

    // --- Game Actions ---
    public async challengeAi(level = 1, limitSeconds = 180, increment = 2) {
        const params = new URLSearchParams();
        params.append('level', level.toString());
        params.append('clock.limit', limitSeconds.toString());
        params.append('clock.increment', increment.toString());

        return this.request('/api/challenge/ai', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
            },
            body: params.toString(),
        });
    }

    public async makeMove(gameId: string, orig: string, dest: string) {
        return this.request(`/api/board/game/${gameId}/move/${orig}${dest}`, {
            method: 'POST',
        });
    }

    public async resignGame(gameId: string) {
        return this.request(`/api/board/game/${gameId}/resign`, {
            method: 'POST',
        });
    }
    public async seekGame(
        rated: boolean,
        variant: string = 'standard',
        time: number = 10,
        increment: number = 5,
        color: 'white' | 'black' | 'random' = 'random',
        ratingMin?: number,
        ratingMax?: number,
    ): Promise<{ id: string }> {
        // const url = `${this.host}/api/board/seek`;
        const body = new URLSearchParams();

        body.set('rated', String(rated));
        body.set('variant', variant);
        body.set('time', String(time));
        body.set('increment', String(increment));
        body.set('color', color);

        if (ratingMin !== undefined) body.set('ratingMin', String(ratingMin));
        if (ratingMax !== undefined) body.set('ratingMax', String(ratingMax));

        const res = await this.request("/api/board/seek", {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: body.toString(),
        });

        if (!res.ok) {
            throw new Error(`Seek failed: ${res.status} ${res.statusText}`);
        }

        // API currently responds with a float (duration).
        const text = await res.text();

        // You can only get the real gameId via streamEvents → `gameStart`.
        // So we generate a temporary id placeholder here:
        return { id: text };
    }
}

export interface CurrentGame {
    gameId: string;
    fullId: string;
    color: string;
    fen: string;
    hasMoved: boolean;
    isMyTurn: boolean;
    lastMove: string;
    opponent: Record<string, any>;
    perf: string;
    rated: boolean;
    secondsLeft: number;
    source: string;
    speed: string;
    variant: Record<string, any>;
}
export interface HttpClient {
    (url: string, options?: RequestInit): Promise<Response>;
}

